import { useEffect, useRef, useState } from 'react';

/**
 * Меню, закрывающееся по нажатию мимо и по Esc.
 *
 * Заведено отдельно, потому что таких меню в карточке два — выбор недели во
 * Входящих и выбор спринта, — и оба закрываются одинаково. Правило, записанное
 * в двух местах, расходится на первой же правке; здесь оно одно.
 */
export interface Menu {
  open: boolean;
  setOpen: (open: boolean | ((was: boolean) => boolean)) => void;
  /** Вешается на обёртку: по ней меню отличает «нажали внутри» от «мимо». */
  box: React.RefObject<HTMLSpanElement | null>;
  /** Обработчик нажатия на кнопку меню: открыть или закрыть. */
  toggle: (event: React.MouseEvent<HTMLElement>) => void;
  /** Закрыть после выбора: фокус с клавиатуры возвращается на кнопку меню. */
  close: () => void;
}

export interface MenuOptions {
  /**
   * Закрывать меню, когда указатель ушёл с ближайшего предка по этому
   * селектору. Нужно меню, чья кнопка видна только при наведении
   * («Запланировать» на карточке доски, 3.1.10): мышь ушла — кнопка
   * спряталась, а раскрытый список без неё висел бы сам по себе (замечание
   * владельца, 2026-09-13).
   */
  closeOnLeave?: string | undefined;
}

/**
 * Фокус был в меню — вернуть его на кнопку, а не уронить в никуда.
 *
 * Только если он действительно внутри: открытое мышью меню фокуса не
 * получает (см. `toggle`), и возвращать там нечего — иначе закрытие мышью
 * рисовало бы на кнопке кольцо, от которого `toggle` как раз избавляет.
 */
function returnFocus(box: HTMLElement | null, trigger: HTMLElement | null): void {
  const active = document.activeElement;
  if (box?.contains(active) === true && active !== trigger) trigger?.focus();
}

export function useMenu({ closeOnLeave }: MenuOptions = {}): Menu {
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLSpanElement>(null);
  const trigger = useRef<HTMLElement | null>(null);
  const focusFirst = useRef(false);

  function toggle(event: React.MouseEvent<HTMLElement>): void {
    trigger.current = event.currentTarget;

    if (open) {
      returnFocus(box.current, trigger.current);
      setOpen(false);
      return;
    }

    // `detail` — число нажатий: у мыши и пальца оно не меньше единицы, а Enter
    // и пробел на кнопке присылают `click` с нулём.
    const byKeyboard = event.detail === 0;
    focusFirst.current = byKeyboard;

    // Открыли мышью — фокус с кнопки снимается. Оставшись на ней, он после
    // любой клавиши (хотя бы Esc) рисовал кольцо, и рядом с янтарным «в
    // текущий» кнопка выглядела вторым выбранным пунктом; а на карточке доски
    // ещё и держал «Запланировать» видимой, когда мышь уже ушла (замечание
    // владельца, 2026-09-13).
    if (!byKeyboard && document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
    setOpen(true);
  }

  function close(): void {
    returnFocus(box.current, trigger.current);
    setOpen(false);
  }

  useEffect(() => {
    if (!open) return;

    // Открыли с клавиатуры — фокус сразу на первый пункт, как у всякой кнопки
    // меню: иначе к пунктам пробираются табом через всё, что стоит между.
    if (focusFirst.current) {
      focusFirst.current = false;
      box.current?.querySelector<HTMLElement>('[role="menuitem"]')?.focus();
    }

    function onAway(event: MouseEvent): void {
      if (!box.current?.contains(event.target as Node)) setOpen(false);
    }
    function onKey(event: KeyboardEvent): void {
      // Esc закрывает меню, а не карточку под ним: у панели свой обработчик
      // Esc, и без остановки события закрылись бы оба сразу.
      if (event.key !== 'Escape') return;
      event.stopPropagation();
      returnFocus(box.current, trigger.current);
      setOpen(false);
    }
    function onLeave(): void {
      returnFocus(box.current, trigger.current);
      setOpen(false);
    }

    // `mouseleave` не срабатывает, пока указатель над самим меню: оно лежит в
    // разметке внутри того же предка, хоть и рисуется ниже его края.
    const area = closeOnLeave === undefined ? null : (box.current?.closest(closeOnLeave) ?? null);

    document.addEventListener('mousedown', onAway);
    document.addEventListener('keydown', onKey, true);
    area?.addEventListener('mouseleave', onLeave);
    return () => {
      document.removeEventListener('mousedown', onAway);
      document.removeEventListener('keydown', onKey, true);
      area?.removeEventListener('mouseleave', onLeave);
    };
  }, [open, closeOnLeave]);

  return { open, setOpen, box, toggle, close };
}
